import { isBlobVideoUrl } from '../../utils/blobMediaType';

/**
 * MediaDisplay - Renders either an image or video based on URL
 *
 * Used by mockup components to display media content.
 * Detects video URLs by file extension and renders appropriate element.
 *
 * Note: The imageUrl prop name is kept for backwards compatibility,
 * but it can contain either image or video URLs (.jpg, .png, .mp4, .mov)
 */

interface MediaDisplayProps {
  /** URL to image or video file */
  url?: string;
  /** MIME type of the asset (e.g. 'video/mp4') — used as fallback when URL has no extension */
  contentType?: string | null;
  /** Alt text for images */
  alt?: string;
  /** CSS class for the media element */
  className?: string;
  /** Placeholder content when no URL provided */
  placeholder?: React.ReactNode;
  /** Aspect ratio label for placeholder (e.g., "1:1", "9:16") */
  aspectRatioLabel?: string;
  /** Recommended dimensions for placeholder */
  recommendedSize?: string;
}

/**
 * Check if a URL points to a video file based on extension or blob type
 */
function isVideoUrl(url: string): boolean {
  const videoExtensions = ['.mp4', '.mov'];
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.startsWith('data:video/')) {
    return true;
  }
  if (lowerUrl.startsWith('blob:')) {
    return isBlobVideoUrl(url);
  }
  return videoExtensions.some((ext) => lowerUrl.includes(ext));
}

export function MediaDisplay({
  url,
  contentType,
  alt = 'Ad media',
  className = 'w-full h-full object-cover',
  placeholder,
  aspectRatioLabel,
  recommendedSize,
}: MediaDisplayProps) {
  // No URL - show placeholder
  if (!url) {
    if (placeholder) {
      return <>{placeholder}</>;
    }
    return (
      <div className="text-center text-gray-400">
        <div className="text-4xl font-bold mb-2">{aspectRatioLabel || '1:1 or 4:5'}</div>
        <div className="text-xs">{recommendedSize || '1080x1080 or 1080x1350px recommended'}</div>
      </div>
    );
  }

  // Video URL - render video element (contentType is the most reliable signal)
  if (contentType?.startsWith('video/') || isVideoUrl(url)) {
    return <video src={url} className={className} autoPlay muted loop playsInline />;
  }

  // Image URL - render img element
  return <img src={url} alt={alt} className={className} />;
}
